import React, { useState, useEffect, useRef } from "react";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageSquare, Send, UserCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function TavernaPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);

  const fetchMessages = () => {
    api.get("/chat/messages")
      .then(res => setMessages(res.data))
      .catch(console.error);
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, []);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    setIsNearBottom(scrollHeight - scrollTop - clientHeight < 50);
  };

  useEffect(() => {
    if (isNearBottom && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  }, [messages, isNearBottom]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await api.post("/chat/messages", { text: newMessage });
      setNewMessage("");
      fetchMessages();
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: "smooth" });
        }
      }, 100);
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail === "Linguaggio non consentito") {
        toast.error("Il messaggio contiene linguaggio non consentito!");
      } else {
        toast.error("Impossibile inviare il messaggio.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-[#181818] flex flex-col h-screen overflow-hidden">
      <Header />
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col min-h-0">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#757169] hover:text-[#2D2A26] dark:text-[#B8B2AA] dark:hover:text-[#F5F1EA] mb-4 transition-colors flex-shrink-0">
          <ArrowLeft size={14} strokeWidth={2.5} /> Dashboard
        </Link>
        
        <div className="flex items-center gap-3 mb-6 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-[#2D2A26] dark:bg-[#F5F1EA] text-white dark:text-[#111111] flex items-center justify-center shadow-md">
            <MessageSquare size={20} strokeWidth={2} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] tracking-tight leading-none">La Taverna</h1>
            <p className="text-sm text-[#757169] dark:text-[#B8B2AA] mt-1">Chiacchiera con gli altri giocatori in tempo reale.</p>
          </div>
        </div>

        <div className="flex-1 bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl flex flex-col overflow-hidden shadow-sm min-h-0">
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex-1 p-4 overflow-y-auto flex flex-col gap-4 custom-scrollbar"
          >
            {messages.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-[#757169] dark:text-[#B8B2AA] text-sm font-semibold">
                Nessun messaggio. Sii il primo a scrivere!
              </div>
            ) : (
              messages.map(m => {
                const isMe = m.user.id === user?.id;
                return (
                  <div key={m.id} className={`flex gap-3 max-w-[80%] ${isMe ? "ml-auto flex-row-reverse" : ""}`}>
                    <div className="w-8 h-8 rounded-full bg-[#EAE3D9] dark:bg-[#2A2A2A] flex items-center justify-center flex-shrink-0 overflow-hidden border border-[#EAE3D9] dark:border-[#333]">
                      {m.user.avatar ? <img src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${m.user.avatar}`} alt="avatar" className="w-full h-full object-cover"/> : <UserCircle2 size={16} className="text-[#757169]" />}
                    </div>
                    <div className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                      <span className="text-[10px] text-[#757169] dark:text-[#B8B2AA] mb-1 font-semibold uppercase tracking-wider">{isMe ? "Tu" : m.user.name}</span>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? "bg-[#2B593F] text-white rounded-tr-sm" : "bg-[#F5F1EA] dark:bg-[#1A1A1A] text-[#2D2A26] dark:text-[#F5F1EA] rounded-tl-sm border border-[#EAE3D9] dark:border-[#2A2A2A]"}`}>
                        {m.text}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="p-4 border-t border-[#EAE3D9] dark:border-[#2A2A2A] bg-[#FDFBF7] dark:bg-[#151515] flex-shrink-0">
            <form onSubmit={handleSend} className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder="Scrivi un messaggio..."
                className="flex-1 h-12 px-4 rounded-xl bg-white dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] text-sm font-semibold text-[#2D2A26] dark:text-[#F5F1EA] focus:outline-none focus:border-[#2B593F] transition-colors"
                disabled={sending}
              />
              <button 
                type="submit" 
                disabled={sending || !newMessage.trim()} 
                className="h-12 px-5 bg-[#2B593F] hover:bg-[#1D3C2A] text-white rounded-xl flex items-center justify-center disabled:opacity-50 transition-colors shadow-sm"
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
